import React, { useCallback, useEffect, useRef, useState } from 'react'
import Video from './Video.tsx'
import type Swing from './types/Swing.tsx'
import PlayerSearch from './PlayerSearch.tsx'
import VideoSettings from './VideoSettings.tsx'
import PlayerCard from './PlayerCard.tsx'
import { TriggerVideoEvent } from './types/globals.ts'
import { TRIGGER_VIDEO_CHANGE } from './constants.ts'
import updateUrl from './update-url.ts'

// Not sure if I want to rely on passing around the same reference
function swingEquals(a: Swing, b: Swing) {
    return a.id === b.id
}

const VIDEO_NATIVE_SPEED = 0.1 // No recollection of if this is right
// const START_SWING_TIME = 0.3
const END_SWING_TIME = 2.8

type AppProps = {
    relevantSwings: Swing[]
    swings: Swing[]
    initPlayerList: Swing[]
    initSelectedIdx: number
}

let setTimeoutId: NodeJS.Timeout

const handlePlayAtSpeed = async (
    $video: HTMLVideoElement,
    currentSpeed: number
) => {
    const playbackRate = (1 / VIDEO_NATIVE_SPEED) * currentSpeed

    $video.playbackRate = playbackRate

    const duration = END_SWING_TIME - $video.currentTime
    const withPlaybackRateDuration = duration / playbackRate

    const playbackPromise = $video.play()

    try {
        await playbackPromise
        // There is no science here, just some experimentation
        // This adds 100ms to the setTimeout no matter what
        const extraWait = (currentSpeed * 1000) / playbackRate

        setTimeoutId = setTimeout(() => {
            $video.pause()
            $video.playbackRate = 1
            $video.play()
            // $video.currentTime = END_SWING_TIME;
        }, withPlaybackRateDuration * 1000 + extraWait)
    } catch (e) {
        // Most likely this is because of the initial page load disallowing autoplay
        console.error(e)
    }
}

const handlePlay = ($video: HTMLVideoElement, currentSpeed: number) => {
    if ($video.paused) {
        if ($video.currentTime < END_SWING_TIME) {
            handlePlayAtSpeed($video, currentSpeed)
            return
        }

        $video.playbackRate = 1
        $video.play()
    } else {
        $video.pause()

        // If we haven't already hit the speed change, clear the oncoming handling
        clearTimeout(setTimeoutId)
    }
}

export default function App({
    relevantSwings,
    swings,
    initPlayerList,
    initSelectedIdx,
}: AppProps) {
    const videoRef = useRef<HTMLVideoElement>(null)
    const hasBeenUpdated = useRef(false)
    const scrubbingState = useState(false)
    const [isScrubbing, setIsScrubbing] = scrubbingState
    const [playerList, setPlayerList] = useState(initPlayerList)
    const [selectedIdx, setSelectedIdx] = useState(initSelectedIdx)
    const [proportionPlayed, setProportionPlayed] = useState(0)
    const autoPlayState = useState(true)
    const [isAutoPlay] = autoPlayState
    const currentSpeedState = useState(0.1)
    const [currentSpeed] = currentSpeedState

    const handleReset = useCallback(() => {
        if (videoRef.current) {
            videoRef.current.pause()
            videoRef.current.currentTime = 0
        }
    }, [])

    const changeSelection = useCallback(
        (newIdx: number) => {
            setSelectedIdx(newIdx)
            setProportionPlayed(0)
        },
        [currentSpeedState]
    )

    const handleSelect = useCallback(
        (selection: Swing) => {
            const idx = playerList.findIndex((p) => swingEquals(p, selection))
            if (idx === selectedIdx && videoRef.current) {
                handlePlay(videoRef.current, currentSpeedState[0])
                setIsScrubbing(false)
                return
            }
            changeSelection(idx)
        },
        [currentSpeedState, playerList, selectedIdx]
    )

    const handleAdd = useCallback(
        (item: Swing) => {
            const exists = playerList.find((i) => swingEquals(item, i))

            if (exists) {
                const existIdx = playerList.findIndex((i) =>
                    swingEquals(item, i)
                )
                changeSelection(existIdx)
                return
            }

            const newList = [item, ...playerList]
            changeSelection(0)
            setPlayerList(newList)
        },
        [playerList, changeSelection, setPlayerList]
    )

    // This setup might have me swearing off react.
    const handleVideoEnd = useCallback(() => {
        if (isAutoPlay && selectedIdx + 1 !== playerList.length) {
            changeSelection(selectedIdx + 1)
        }
    }, [selectedIdx, playerList])

    const handleTimeUpdate = useCallback(() => {
        if (!videoRef.current) {
            return
        }

        const proportion =
            videoRef.current.currentTime / videoRef.current.duration

        setProportionPlayed(proportion)
    }, [selectedIdx, playerList])

    useEffect(() => {
        function handleTriggerVideo(e: TriggerVideoEvent) {
            const { playerId, season, batSide } = e.detail
            const swing = swings.find(
                (s) =>
                    s.playerId === playerId &&
                    s.batSide === batSide &&
                    s.year === season
            )

            if (swing && videoRef.current) {
                videoRef.current.scrollIntoView({
                    behavior: 'smooth',
                })
                handleAdd(swing)
            }
        }

        window.addEventListener(TRIGGER_VIDEO_CHANGE, handleTriggerVideo)

        return () =>
            window.removeEventListener(TRIGGER_VIDEO_CHANGE, handleTriggerVideo)
    }, [handleAdd])

    useEffect(() => {
        const $video = videoRef.current

        if (!$video || !isScrubbing) {
            return
        }

        $video.pause()

        if ($video.currentTime >= END_SWING_TIME) {
            $video.currentTime = 0
        } else {
            // Try to force a time update
            $video.currentTime += 0.0001
        }
    }, [isScrubbing])

    useEffect(() => {
        if (!videoRef.current) {
            return () => {}
        }

        videoRef.current.addEventListener('ended', handleVideoEnd)
        videoRef.current.addEventListener('timeupdate', handleTimeUpdate)

        return () => {
            videoRef.current?.removeEventListener('ended', handleVideoEnd)
            videoRef.current?.removeEventListener(
                'timeupdate',
                handleTimeUpdate
            )
        }
    }, [handleVideoEnd, handleTimeUpdate])

    useEffect(() => {
        if (hasBeenUpdated.current) {
            updateUrl(playerList, selectedIdx)
        }
        hasBeenUpdated.current = true
    }, [playerList, selectedIdx])

    const handleVideoChange = useCallback(() => {
        if (!videoRef.current) {
            return
        }

        const $video = videoRef.current

        if (isAutoPlay && !isScrubbing) {
            // Play
            handlePlay($video, currentSpeed)
        }
    }, [currentSpeed, isAutoPlay, isScrubbing])

    const handleRemove = useCallback(
        (swing: Swing) => {
            const idx = playerList.findIndex((s) => s.id === swing.id)

            if (idx === -1) {
                return
            }

            if (selectedIdx === idx) {
                setSelectedIdx(Math.max(0, selectedIdx - 1))
            } else if (selectedIdx === playerList.length - 1) {
                setSelectedIdx(selectedIdx - 1)
            }

            setPlayerList([
                ...playerList.slice(0, idx),
                ...playerList.slice(idx + 1),
            ])
        },
        [playerList, setPlayerList, selectedIdx, setSelectedIdx]
    )

    return (
        <div>
            <Video
                ref={videoRef}
                isScrubbing={isScrubbing}
                selected={playerList[selectedIdx]}
                setIsScrubbing={setIsScrubbing}
                handleChange={handleVideoChange}
            />
            <div
                style={{
                    backgroundColor: 'rgb(4, 30, 66)',
                    color: 'white',
                    padding: '0 4px 8px',
                }}
            >
                <div
                    style={{
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        display: 'flex',
                        padding: '4px',
                    }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {/* Add Player */}
                        <PlayerSearch
                            relevantSwings={relevantSwings}
                            swings={swings}
                            onAdd={handleAdd}
                        />
                        {/* Settings */}
                        <VideoSettings
                            autoPlayState={autoPlayState}
                            currentSpeedState={currentSpeedState}
                            scrubbingState={scrubbingState}
                        />
                    </div>

                    {playerList.map((info, i) => {
                        const selected = i === selectedIdx
                        return (
                            <PlayerCard
                                key={info.id}
                                handleRemove={handleRemove}
                                handleReset={handleReset}
                                info={info}
                                selected={selected}
                                onSelect={handleSelect}
                                proportionPlayed={
                                    selected ? proportionPlayed : 0
                                }
                            />
                        )
                    })}
                </div>
            </div>
        </div>
    )
}
